import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import Form, { GroupItem, SimpleItem } from 'devextreme-react/form';
import { Column, Editing, Lookup, RequiredRule, Button as GridButton, Toolbar, Item as ToolbarItem } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import { SquireDataGrid } from '../squire-data-grid';
import { ContactPicker } from './contact-picker';
import { NewContactNameFields } from './new-contact-name-fields';
import { CompanySelectorWithCreate } from './company-selector-with-create';
import { pickDefaultPhoneTypeId } from './phone-type-default';
import { ContactEditorApiService } from '~/services';

const CONTACT_STATUSES = [
  { id: 1, value: 'Current' },
  { id: 2, value: 'Former' },
];

const BLUR_CHANGE = { valueChangeEvent: 'blur' };

export function CompanyContactEditor({
  companyContact,
  isCompanySide = false,
  // Which half of the company form's "Add Contact" drop-down opened this: 'existing' picks from every
  // contact in the system, 'new' asks for a name and creates the Contact first. Company side only —
  // the contact side always picks a company (WK-565).
  createMode = 'existing',
  companyId,
  phoneTypesDataSource,
  projectContactTypesDataSource,
  apiService,
  dtoCallbacks,
  onCreate,
  onCancelCreate,
  hideInternalActions = false,
  onCreateStateChange,
}) {
  const isCreate = !companyContact;

  const [createData, setCreateData] = useState(() => isCreate ? {
    contactId: null,
    contactName: '',
    companyId: isCompanySide ? companyId : null,
    companyName: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    location: '',
    roleId: null,
    statusId: 1,
  } : null);
  const [createPhones, setCreatePhones] = useState([]);
  const [createEmails, setCreateEmails] = useState([]);
  const [createPhonesKey, setCreatePhonesKey] = useState(0);
  const [createEmailsKey, setCreateEmailsKey] = useState(0);

  const [phoneNumbers, setPhoneNumbers] = useState(() =>
    companyContact?.phoneNumbers?.$values ?? companyContact?.phoneNumbers ?? []
  );
  const [emailAddresses, setEmailAddresses] = useState(() =>
    companyContact?.emailAddresses?.$values ?? companyContact?.emailAddresses ?? []
  );
  const [phonesKey, setPhonesKey] = useState(0);
  const [emailsKey, setEmailsKey] = useState(0);

  const internalApiService = useMemo(() => apiService ?? new ContactEditorApiService(), [apiService]);
  const rolesDs = useMemo(
    () => projectContactTypesDataSource ?? internalApiService.getProjectContactTypesDataSource(),
    [projectContactTypesDataSource, internalApiService]
  );
  const phonesDs = useMemo(
    () => phoneTypesDataSource ?? internalApiService.getPhoneTypesDataSource(),
    [phoneTypesDataSource, internalApiService]
  );
  // Phone type is required (the ContactPhoneNumbers.PhoneTypeId FK is non-nullable). Load the
  // available types and default a new phone row to a mobile-like type so the user isn't forced to
  // pick one before saving. Held in a ref so onInitNewRow reads the latest without re-binding.
  const defaultPhoneTypeIdRef = useRef(null);

  // The loaded items also back the phone-type grid <Lookup>. A column <Lookup> accepts a
  // store/array/config but not a DataSource instance (E1016) — and unwrapping the DataSource via
  // .store() dropped its `sort: "value"`, since sorting is a DataSource-level concern the store
  // knows nothing about. That left the phone-type dropdown unordered (WK-565). Binding to the
  // items that DataSource.load() returns keeps the sort.
  const [phoneTypes, setPhoneTypes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const loaded = typeof phonesDs?.load === 'function' ? phonesDs.load() : phonesDs;
    Promise.resolve(loaded).then((result) => {
      if (cancelled) return;
      const items = Array.isArray(result) ? result : result?.data ?? [];
      defaultPhoneTypeIdRef.current = pickDefaultPhoneTypeId(items);
      setPhoneTypes(items);
    });
    return () => {
      cancelled = true;
    };
  }, [phonesDs]);

  const handlePhoneInitNewRow = useCallback((e) => {
    e.data.phoneTypeId = defaultPhoneTypeIdRef.current;
  }, []);

  // --- Create mode ---

  function handleCreateFieldChanged(e) {
    setCreateData(prev => ({ ...prev, [e.dataField]: e.value }));
  }

  function handleCreatePhoneInserting(e) {
    e.cancel = true;
    setCreatePhones(prev => [...prev, { _localId: Date.now(), number: e.data.number, phoneTypeId: e.data.phoneTypeId ?? null, preferred: prev.length === 0 }]);
    setCreatePhonesKey(k => k + 1);
  }

  function handleCreatePhoneRemoving(e) {
    e.cancel = true;
    setCreatePhones(prev => prev.filter(p => p._localId !== e.data._localId));
    setCreatePhonesKey(k => k + 1);
  }

  function handleCreateEmailInserting(e) {
    e.cancel = true;
    setCreateEmails(prev => [...prev, { _localId: Date.now(), address: e.data.address, preferred: prev.length === 0 }]);
    setCreateEmailsKey(k => k + 1);
  }

  function handleCreateEmailRemoving(e) {
    e.cancel = true;
    setCreateEmails(prev => prev.filter(em => em._localId !== e.data._localId));
    setCreateEmailsKey(k => k + 1);
  }

  function handleCreatePhoneUpdating(e) {
    const updated = { ...e.oldData, ...e.newData };
    setCreatePhones(prev => prev.map(p => {
      if (p._localId === e.oldData._localId) return { ...p, ...e.newData };
      if (updated.preferred) return { ...p, preferred: false };
      return p;
    }));
  }

  function handleCreateEmailUpdating(e) {
    const updated = { ...e.oldData, ...e.newData };
    setCreateEmails(prev => prev.map(em => {
      if (em._localId === e.oldData._localId) return { ...em, ...e.newData };
      if (updated.preferred) return { ...em, preferred: false };
      return em;
    }));
  }

  const isNewContact = isCreate && isCompanySide && createMode === 'new';

  const canConfirmCreate = isCreate && (
    isCompanySide
      ? (isNewContact ? !!createData?.firstName && !!createData?.lastName : !!createData?.contactId)
      : !!createData?.companyId
  );

  // Reports what the user entered and lets the parent decide how to persist it: the company form
  // stages the association into the company DTO, while the estimate creates it immediately. Awaited
  // so a parent whose save fails keeps its popup open. For a new contact, contactId is 0 and the
  // names carry the payload instead (WK-565).
  async function handleConfirmCreate() {
    if (!canConfirmCreate) return;

    await onCreate({
      contactId: createData.contactId ?? 0,
      companyId: (isCompanySide ? companyId : createData.companyId) ?? 0,
      contactName: createData.contactName,
      companyName: createData.companyName,
      firstName: isNewContact ? createData.firstName : undefined,
      lastName: isNewContact ? createData.lastName : undefined,
      jobTitle: createData.jobTitle,
      location: createData.location,
      roleId: createData.roleId,
      statusId: createData.statusId ?? 1,
      // Carry a negative id (the "new row" convention used by the edit-mode grids) so that once this
      // contact is staged onto the parent grid, expanding its detail binds the phone/email grids
      // (keyExpr="id") to objects that actually have a key — otherwise DevExtreme throws E1046. The
      // backend ignores ids on toCreate entries, so this is display-only plumbing.
      phoneNumbers: { toCreate: createPhones.map(({ _localId, ...p }) => ({ ...p, id: -_localId })), toUpdate: {}, toDelete: [] },
      emailAddresses: { toCreate: createEmails.map(({ _localId, ...e }) => ({ ...e, id: -_localId })), toUpdate: {}, toDelete: [] },
    });
  }

  useEffect(() => {
    if (!isCreate || !onCreateStateChange) return;
    onCreateStateChange({ canConfirm: canConfirmCreate, confirm: handleConfirmCreate });
    // handleConfirmCreate is recreated every render but closes over the latest
    // create state, so re-publishing on every relevant change is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, canConfirmCreate, createData, createPhones, createEmails, onCreateStateChange]);

  // --- Edit mode ---

  function handleEditFieldChanged(e) {
    dtoCallbacks?.onFieldChanged(e.dataField, e.value);
  }

  // Editing a phone/email calls markChanged() on the parent, which re-renders this editor; because it
  // lives in a DevExtreme master-detail, that can remount it and reset local state. So we keep the
  // working list on companyContact too — a parent-owned view-model object that survives the remount —
  // and re-seed from it (see the useState initializers above). This mirrors the addresses tab, which
  // pushes new rows into the company view model. `refreshGrid` bumps the grid key so an inserted/
  // removed row shows immediately when the editor does NOT remount.
  function persistPhones(next, refreshGrid) {
    setPhoneNumbers(next);
    if (refreshGrid) setPhonesKey(k => k + 1);
    if (companyContact) companyContact.phoneNumbers = next;
  }

  function persistEmails(next, refreshGrid) {
    setEmailAddresses(next);
    if (refreshGrid) setEmailsKey(k => k + 1);
    if (companyContact) companyContact.emailAddresses = next;
  }

  function handlePhoneInserting(e) {
    e.cancel = true;
    const phone = { number: e.data.number, phoneTypeId: e.data.phoneTypeId ?? null, preferred: phoneNumbers.length === 0 };
    const newEntry = { ...phone, id: -Date.now() };
    dtoCallbacks?.onPhoneCreated(newEntry);
    persistPhones([...phoneNumbers, newEntry], true);
  }

  function handlePhoneUpdating(e) {
    const updated = { ...e.oldData, ...e.newData };
    const values = { number: updated.number, phoneTypeId: updated.phoneTypeId ?? null, preferred: updated.preferred ?? false };
    dtoCallbacks?.onPhoneUpdated(e.oldData.id, values);
    persistPhones(phoneNumbers.map(p => {
      if (p.id === e.oldData.id) return { ...p, ...e.newData };
      if (updated.preferred) return { ...p, preferred: false };
      return p;
    }), false);
  }

  function handlePhoneRemoving(e) {
    e.cancel = true;
    dtoCallbacks?.onPhoneDeleted(e.data);
    persistPhones(phoneNumbers.filter(p => p.id !== e.data.id), true);
  }

  function handleEmailInserting(e) {
    e.cancel = true;
    const email = { address: e.data.address, preferred: emailAddresses.length === 0 };
    const newEntry = { ...email, id: -Date.now() };
    dtoCallbacks?.onEmailCreated(newEntry);
    persistEmails([...emailAddresses, newEntry], true);
  }

  function handleEmailUpdating(e) {
    const updated = { ...e.oldData, ...e.newData };
    const values = { address: updated.address, preferred: updated.preferred ?? false };
    dtoCallbacks?.onEmailUpdated(e.oldData.id, values);
    persistEmails(emailAddresses.map(em => {
      if (em.id === e.oldData.id) return { ...em, ...e.newData };
      if (updated.preferred) return { ...em, preferred: false };
      return em;
    }), false);
  }

  function handleEmailRemoving(e) {
    e.cancel = true;
    dtoCallbacks?.onEmailDeleted(e.data);
    persistEmails(emailAddresses.filter(em => em.id !== e.data.id), true);
  }

  if (isCreate) {
    return (
      <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '12px', marginBottom: '12px' }}>
        <div style={{ marginBottom: '8px' }}>
          <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
            {isCompanySide ? (isNewContact ? 'New Contact' : 'Contact') : 'Company'}
          </label>
          {isCompanySide && isNewContact ? (
            <NewContactNameFields
              firstName={createData.firstName}
              lastName={createData.lastName}
              onChange={(field, value) => setCreateData(prev => ({ ...prev, [field]: value }))}
            />
          ) : isCompanySide ? (
            <ContactPicker
              value={createData.contactId}
              onSelectionChanged={(e) => setCreateData(prev => ({
                ...prev,
                contactId: e.selectedItem?.contactId ?? null,
                contactName: e.selectedItem?.fullName ?? '',
              }))}
            />
          ) : (
            <CompanySelectorWithCreate
              apiService={internalApiService}
              value={createData.companyId}
              onValueChanged={(e) => setCreateData(prev => ({
                ...prev,
                companyId: e.selectedItem?.id ?? null,
                companyName: e.selectedItem?.displayName ?? '',
              }))}
            />
          )}
        </div>

        <Form formData={createData} colCount={2} labelLocation="left" onFieldDataChanged={handleCreateFieldChanged}>
          <GroupItem>
            <SimpleItem dataField="jobTitle" label={{ text: 'Job Title' }} editorOptions={BLUR_CHANGE} />
            <SimpleItem dataField="location" label={{ text: 'Location' }} editorOptions={BLUR_CHANGE} />
          </GroupItem>
          <GroupItem>
            <SimpleItem dataField="roleId" label={{ text: 'Role' }} editorType="dxSelectBox"
              editorOptions={{ dataSource: rolesDs, displayExpr: 'value', valueExpr: 'id' }} />
            <SimpleItem dataField="statusId" label={{ text: 'Status' }} editorType="dxSelectBox"
              editorOptions={{ dataSource: CONTACT_STATUSES, displayExpr: 'value', valueExpr: 'id' }} />
          </GroupItem>
        </Form>

        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <SquireDataGrid key={createPhonesKey} dataSource={createPhones} keyExpr="_localId" showBorders={true}
              onInitNewRow={handlePhoneInitNewRow}
              onRowInserting={handleCreatePhoneInserting}
              onRowUpdating={handleCreatePhoneUpdating}
              onRowRemoving={handleCreatePhoneRemoving}
            >
              <Editing mode="row" allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true} />
              <Toolbar>
                <ToolbarItem location="before" render={() => <span style={{ fontWeight: '600', fontSize: '13px' }}>Phone Numbers</span>} />
                <ToolbarItem name="addRowButton" />
              </Toolbar>
              <Column type="buttons" width={100}>
                <GridButton name="edit" />
                <GridButton name="delete" />
                <GridButton name="save" />
                <GridButton name="cancel" />
              </Column>
              <Column dataField="number" caption="Number" minWidth={100} />
              <Column dataField="phoneTypeId" caption="Type" width={120}>
                <Lookup dataSource={phoneTypes} valueExpr="id" displayExpr="value" />
                <RequiredRule message="Phone type is required" />
              </Column>
              <Column dataField="preferred" caption="Pref" dataType="boolean" width={75} />
            </SquireDataGrid>
          </div>
          <div>
            <SquireDataGrid key={createEmailsKey} dataSource={createEmails} keyExpr="_localId" showBorders={true}
              onRowInserting={handleCreateEmailInserting}
              onRowUpdating={handleCreateEmailUpdating}
              onRowRemoving={handleCreateEmailRemoving}
            >
              <Editing mode="row" allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true} />
              <Toolbar>
                <ToolbarItem location="before" render={() => <span style={{ fontWeight: '600', fontSize: '13px' }}>Email Addresses</span>} />
                <ToolbarItem name="addRowButton" />
              </Toolbar>
              <Column type="buttons" width={100}>
                <GridButton name="edit" />
                <GridButton name="delete" />
                <GridButton name="save" />
                <GridButton name="cancel" />
              </Column>
              <Column dataField="address" caption="Address" minWidth={100} />
              <Column dataField="preferred" caption="Pref" dataType="boolean" width={75} />
            </SquireDataGrid>
          </div>
        </div>

        {!hideInternalActions && (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
            <Button text="Add" type="default" stylingMode="outlined" onClick={handleConfirmCreate}
              disabled={!canConfirmCreate} />
            <Button text="Cancel" stylingMode="outlined" onClick={onCancelCreate} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '12px' }}>
      <Form formData={companyContact} colCount={2} labelLocation="left" onFieldDataChanged={handleEditFieldChanged}>
        <GroupItem>
          <SimpleItem dataField="jobTitle" label={{ text: 'Job Title' }} editorOptions={BLUR_CHANGE} />
          <SimpleItem dataField="location" label={{ text: 'Location' }} editorOptions={BLUR_CHANGE} />
        </GroupItem>
        <GroupItem>
          <SimpleItem dataField="roleId" label={{ text: 'Role' }} editorType="dxSelectBox"
            editorOptions={{ dataSource: rolesDs, displayExpr: 'value', valueExpr: 'id' }} />
          <SimpleItem dataField="statusId" label={{ text: 'Status' }} editorType="dxSelectBox"
            editorOptions={{ dataSource: CONTACT_STATUSES, displayExpr: 'value', valueExpr: 'id' }} />
        </GroupItem>
      </Form>

      <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <SquireDataGrid key={phonesKey} dataSource={phoneNumbers} keyExpr="id" showBorders={true}
            onInitNewRow={handlePhoneInitNewRow}
            onRowInserting={handlePhoneInserting}
            onRowUpdating={handlePhoneUpdating}
            onRowRemoving={handlePhoneRemoving}
          >
            <Editing mode="row" allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true} />
            <Toolbar>
              <ToolbarItem location="before" render={() => <span style={{ fontWeight: '600', fontSize: '13px' }}>Phone Numbers</span>} />
              <ToolbarItem name="addRowButton" />
            </Toolbar>
            <Column type="buttons" width={100}>
              <GridButton name="edit" />
              <GridButton name="delete" />
              <GridButton name="save" />
              <GridButton name="cancel" />
            </Column>
            <Column dataField="number" caption="Number" minWidth={100} />
            <Column dataField="phoneTypeId" caption="Type" width={120}>
              <Lookup dataSource={phoneTypes} valueExpr="id" displayExpr="value" />
              <RequiredRule message="Phone type is required" />
            </Column>
            <Column dataField="preferred" caption="Pref" dataType="boolean" width={75} />
          </SquireDataGrid>
        </div>
        <div>
          <SquireDataGrid key={emailsKey} dataSource={emailAddresses} keyExpr="id" showBorders={true}
            onRowInserting={handleEmailInserting}
            onRowUpdating={handleEmailUpdating}
            onRowRemoving={handleEmailRemoving}
          >
            <Editing mode="row" allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true} />
            <Toolbar>
              <ToolbarItem location="before" render={() => <span style={{ fontWeight: '600', fontSize: '13px' }}>Email Addresses</span>} />
              <ToolbarItem name="addRowButton" />
            </Toolbar>
            <Column type="buttons" width={100}>
              <GridButton name="edit" />
              <GridButton name="delete" />
              <GridButton name="save" />
              <GridButton name="cancel" />
            </Column>
            <Column dataField="address" caption="Address" minWidth={100} />
            <Column dataField="preferred" caption="Pref" dataType="boolean" width={75} />
          </SquireDataGrid>
        </div>
      </div>
    </div>
  );
}
